import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface OnboardingState {
  onboarding_seen_at: string | null;
  onboarding_completed_at: string | null;
  checklist_dismissed_at: string | null;
}

export function useArtistOnboardingState() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<OnboardingState | null>({
    queryKey: ["artist-onboarding-state", user?.id],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("artist_profiles")
        .select(
          "onboarding_seen_at, onboarding_completed_at, checklist_dismissed_at",
        )
        .eq("id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data as OnboardingState | null;
    },
  });

  const update = useMutation({
    mutationFn: async (patch: Partial<OnboardingState>) => {
      if (!user?.id) return;
      const { error } = await supabase
        .from("artist_profiles")
        .update(patch)
        .eq("id", user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["artist-onboarding-state", user?.id] });
    },
  });

  return {
    state: data ?? null,
    isLoading,
    markSeen: () =>
      update.mutate({ onboarding_seen_at: new Date().toISOString() }),
    markCompleted: () =>
      update.mutate({
        onboarding_completed_at: new Date().toISOString(),
        onboarding_seen_at: data?.onboarding_seen_at ?? new Date().toISOString(),
      }),
    dismissChecklist: () =>
      update.mutate({ checklist_dismissed_at: new Date().toISOString() }),
    resetForTour: () =>
      update.mutate({
        onboarding_seen_at: null,
        checklist_dismissed_at: null,
      }),
  };
}
